import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Alert, Badge, Loader, Paper, Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { formatHumanName, normalizeErrorString } from '@medplum/core'
import { formatPatientName } from '@/utils'
import { useMedplumProfile } from '@medplum/react'
import { useSubscription } from '@medplum/react-hooks'
import { IconInbox } from '@tabler/icons-react'
import { useMiddlewareClient } from '@/hooks'
import { formatDateTime } from '@/utils'
import { logError } from '@/utils/errorUtils'
import {
  getThreadLabels,
  getCommunicationFromSubscriptionBundle,
  isThreadHeaderCommunication,
  isArchivedThread,
  THREAD_LABEL_FOLLOW_UP,
  THREAD_LABEL_URGENT,
  type MessageThreadSummary,
  type PractitionerThreadOption,
  type ThreadStatus,
  type MiddlewareThreadSummary,
} from '../service'
import { ThreadsFilterOptions } from './FilterControls'
import { ThreadList } from './ThreadList'
import { ThreadTabs, threadTabId, type ThreadTab } from './ThreadTabs'

type MyThreadsProps = {
  refreshKey?: number
  selectedThreadId?: string
  threadStatusMap?: Record<string, ThreadStatus>
  onThreadStatusMapChange?: (value: Record<string, ThreadStatus>) => void
  onSelectThread?: (thread: MessageThreadSummary) => void
  /** A thread to open once the list holds it (a link from the daily briefing). */
  focusThreadId?: string | null
  threadsFilterOptions?: ThreadsFilterOptions
  onThreadsFilterOptionsChange?: (value: ThreadsFilterOptions) => void
  /**
   * Renders the compact card list for the patient chart's drawer, rather than
   * the full Messages page's `ThreadList`. Both now share the Active/Archived
   * tabs; what differs is the row design — see MESSAGES_V2_DESIGN.md §2.1.
   */
  minimized?: boolean
  /** Controls for the end of the tab row (minimized only) — see `ThreadTabs`. */
  headerActions?: ReactNode
}

/** The drawer's tab panel — namespaces its tab ids away from the full page's. */
const MINIMIZED_PANEL_ID = 'minimized-thread-tabpanel'

function getPractitionerLabel(practitioner: PractitionerThreadOption | undefined): string {
  if (!practitioner?.practitioner) {
    return 'Unknown practitioner'
  }

  return (
    formatHumanName(practitioner.practitioner.name?.[0]) ||
    practitioner.practitioner.id ||
    'Unknown practitioner'
  )
}

export const MyThreads = memo(
  ({
    refreshKey = 0,
    selectedThreadId,
    threadStatusMap = {},
    onThreadStatusMapChange,
    onSelectThread,
    focusThreadId,
    threadsFilterOptions,
    onThreadsFilterOptionsChange,
    minimized = false,
    headerActions,
  }: MyThreadsProps) => {
    const middleware = useMiddlewareClient()
    const profile = useMedplumProfile()
    const practitionerProfile = profile?.resourceType === 'Practitioner' ? profile : undefined

    const [threads, setThreads] = useState<MiddlewareThreadSummary[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [initialLoadComplete, setInitialLoadComplete] = useState(false)
    const [tab, setTab] = useState<ThreadTab>('active')

    const reloadThreads = useCallback(async (): Promise<void> => {
      if (!practitionerProfile?.id) {
        setThreads([])
        setError('Current Medplum profile is not a practitioner.')
        setInitialLoadComplete(false)
        return
      }

      setLoading(true)
      setError(null)
      try {
        const payload: {
          patientId?: string
          onlyUrgent?: boolean
          onlyFollowUps?: boolean
        } = {}

        if (threadsFilterOptions?.selectedPatient?.id) {
          payload.patientId = threadsFilterOptions.selectedPatient.id
        }

        if (threadsFilterOptions?.showUrgentOnly) {
          payload.onlyUrgent = true
        }

        if (threadsFilterOptions?.showFollowUpOnly) {
          payload.onlyFollowUps = true
        }

        const result = (await middleware.get('/threads', {
          ...payload,
        })) as MiddlewareThreadSummary[]
        const nextThreadStatusMap = result.reduce<Record<string, ThreadStatus>>((acc, item) => {
          if (!item.thread.id) {
            return acc
          }

          acc[item.thread.id] = item.status
          return acc
        }, {})

        onThreadStatusMapChange?.(nextThreadStatusMap)
        setThreads(result)
      } catch (err) {
        setError(normalizeErrorString(err))
      } finally {
        setInitialLoadComplete(true)
        setLoading(false)
      }
    }, [
      middleware,
      onThreadStatusMapChange,
      practitionerProfile?.id,
      threadsFilterOptions?.selectedPatient,
      threadsFilterOptions?.showUrgentOnly,
      threadsFilterOptions?.showFollowUpOnly,
    ])

    useEffect(() => {
      if (!practitionerProfile?.id) {
        setThreads([])
        setError('Current Medplum profile is not a practitioner.')
        setInitialLoadComplete(false)
        return
      }

      async function loadThreads(): Promise<void> {
        await reloadThreads()
      }

      loadThreads().catch((err) => logError('messages', 'Failed to load threads', err))
    }, [practitionerProfile?.id, refreshKey, reloadThreads, threadsFilterOptions])

    useSubscription(
      practitionerProfile?.id
        ? `Communication?recipient=Practitioner/${practitionerProfile.id}`
        : undefined,
      (bundle) => {
        if (isThreadHeaderCommunication(getCommunicationFromSubscriptionBundle(bundle))) {
          reloadThreads().catch((err) =>
            logError('messages', 'Failed to reload threads on recipient subscription', err),
          )
        }
      },
    )

    useSubscription(
      practitionerProfile?.id
        ? `Communication?sender=Practitioner/${practitionerProfile.id}`
        : undefined,
      (bundle) => {
        if (isThreadHeaderCommunication(getCommunicationFromSubscriptionBundle(bundle))) {
          reloadThreads().catch((err) =>
            logError('messages', 'Failed to reload threads on sender subscription', err),
          )
        }
      },
    )

    // The focus thread already opened, so a reload or another pick doesn't reopen it.
    const focusedRef = useRef<string | null>(null)
    useEffect(() => {
      if (threads.length === 0 || !onSelectThread) {
        return
      }

      // A thread the list was opened for: the user asked for it, so it opens even in the
      // patient-chart panel.
      if (focusThreadId && focusedRef.current !== focusThreadId) {
        const focused = threads.find((item) => item.thread.id === focusThreadId)
        if (focused) {
          focusedRef.current = focusThreadId
          onSelectThread(focused)
          return
        }
      }

      // Only pick a thread when nothing is open at all. Selecting a fallback
      // whenever the current id is missing from the list would fight a
      // just-created thread, whose id lands before the reload that contains it.
      //
      // Never in the patient-chart panel: opening a thread marks it read, and
      // nothing the user did there asked for a conversation to be opened.
      if (!selectedThreadId && !minimized) {
        onSelectThread(threads.find((item) => !isArchivedThread(item.thread)) ?? threads[0])
        return
      }

      // Re-publish the selected thread whenever the list reloads. Read receipts
      // live on the thread header, so the open conversation would otherwise keep
      // rendering the header it was opened with and never show "Read".
      const selectedThread = threads.find((item) => item.thread.id === selectedThreadId)
      if (selectedThread) {
        onSelectThread(selectedThread)
      }
    }, [threads, selectedThreadId, onSelectThread, minimized, focusThreadId])

    // The list loaded without the thread the link asked for (archived since, or past the
    // list's 500-thread cap): say so rather than leave the panel looking unanswered. A failed
    // load keeps the link pending, so a later successful reload can still open it.
    useEffect(() => {
      if (!focusThreadId || !initialLoadComplete || loading || error) return
      if (focusedRef.current === focusThreadId) return
      if (threads.some((item) => item.thread.id === focusThreadId)) return
      focusedRef.current = focusThreadId
      showNotification({
        color: 'yellow',
        title: 'Conversation not found',
        message: "It isn't in this patient's messages list anymore.",
      })
    }, [focusThreadId, initialLoadComplete, loading, error, threads])

    const activeThreads = threads.filter((item) => !isArchivedThread(item.thread))
    const archivedThreads = threads.filter((item) => isArchivedThread(item.thread))

    const renderThreadList = (items: MiddlewareThreadSummary[], emptyLabel: string) => {
      if (items.length === 0) {
        return (
          <Paper withBorder radius="md" className="border-dashed p-4 text-center">
            <Text size="sm" c="dimmed">
              {emptyLabel}
            </Text>
          </Paper>
        )
      }

      return items.map(({ thread, sender, recipients, latestMessage, relatedPatient }) => {
        const otherParticipants = [sender, ...recipients].filter(
          (participant): participant is PractitionerThreadOption =>
            Boolean(
              participant &&
              participant.practitioner.id &&
              participant.practitioner.id !== practitionerProfile?.id,
            ),
        )
        const updatedAt = latestMessage?.sent || thread.meta?.lastUpdated || thread.sent || ''
        const isSelected = thread.id === selectedThreadId
        const threadStatus = thread.id ? threadStatusMap[thread.id] : undefined
        const threadLabels = getThreadLabels(thread)
        const isUrgent = threadLabels.includes(THREAD_LABEL_URGENT)
        const isFollowUp = threadLabels.includes(THREAD_LABEL_FOLLOW_UP)
        const patientName = relatedPatient ? relatedPatient.name?.[0] : null

        return (
          <Paper
            key={thread.id}
            withBorder
            radius="md"
            className="cursor-pointer p-4 transition hover:bg-slate-50"
            style={{
              backgroundColor: isSelected ? '#e8f1ff' : undefined,
            }}
            onClick={() => onSelectThread?.({ thread, sender, recipients, relatedPatient })}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    {threadStatus === 'unread' && (
                      <span
                        className="inline-block h-3 w-3 rounded-full bg-blue-500"
                        aria-label="Unread thread"
                      />
                    )}
                    <Text fw={600} c={isSelected ? 'blue.9' : undefined} truncate>
                      {thread.topic?.text || 'Untitled thread'}
                    </Text>
                  </div>
                  <div className="flex items-center gap-2">
                    {isUrgent && (
                      <Badge
                        radius="xl"
                        size="md"
                        variant="light"
                        styles={{
                          root: {
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            fontWeight: 600,
                            fontSize: '13px',
                            paddingInline: '12px',
                            height: '28px',
                            textTransform: 'none',
                          },
                        }}
                      >
                        Urgent
                      </Badge>
                    )}
                    {isFollowUp && (
                      <Badge
                        radius="xl"
                        size="md"
                        variant="light"
                        styles={{
                          root: {
                            backgroundColor: '#fef3c7',
                            color: '#d97706',
                            border: 'none',
                            fontWeight: 600,
                            fontSize: '13px',
                            paddingInline: '12px',
                            height: '28px',
                            textTransform: 'none',
                          },
                        }}
                      >
                        Follow Up
                      </Badge>
                    )}
                  </div>
                </div>

                <Text size="sm" c="dimmed">
                  With{' '}
                  {otherParticipants.length > 0
                    ? otherParticipants
                        .map((participant) => `${getPractitionerLabel(participant)}`)
                        .join(', ')
                    : 'no other participants'}
                </Text>

                {isArchivedThread(thread) && (
                  <Badge variant="light" color="gray" radius="sm">
                    Archived
                  </Badge>
                )}

                {relatedPatient && (
                  <Text size="sm">
                    Patient: {` ${formatPatientName(patientName ?? undefined)}`}
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  {/* Not "received": this is the last message's own timestamp,
                      which for an outgoing message is when it was sent. */}
                  {updatedAt ? `Last message: ${formatDateTime(updatedAt, '-', true)}` : ''}
                </Text>
              </div>
            </div>
          </Paper>
        )
      })
    }

    if (loading && !initialLoadComplete) {
      return (
        <Paper withBorder radius="md" className="h-full min-h-[240px] p-6">
          <div className="flex h-full items-center justify-center">
            <Loader />
          </div>
        </Paper>
      )
    }

    if (error) {
      return <Alert color="red">{error}</Alert>
    }

    if (!minimized) {
      return (
        <ThreadList
          threads={threads}
          selectedThreadId={selectedThreadId}
          currentPractitionerId={practitionerProfile?.id}
          onSelectThread={onSelectThread}
          threadsFilterOptions={threadsFilterOptions}
          onThreadsFilterOptionsChange={onThreadsFilterOptionsChange}
        />
      )
    }

    if (threads.length === 0) {
      return (
        <Paper withBorder radius="md" className="h-full p-8">
          <div className="flex flex-col items-center justify-center gap-3 text-center text-[#7D7987]">
            <IconInbox size={28} />
            <Text fw={600} c="dark">
              No threads yet
            </Text>
            <Text size="sm">Start a new practitioner conversation to see it here.</Text>
          </div>
        </Paper>
      )
    }

    return (
      <Paper withBorder radius="md" className="flex h-full flex-col overflow-hidden">
        <ThreadTabs
          tab={tab}
          onTabChange={setTab}
          activeCount={activeThreads.length}
          archivedCount={archivedThreads.length}
          panelId={MINIMIZED_PANEL_ID}
          actions={headerActions}
        />
        <div
          role="tabpanel"
          id={MINIMIZED_PANEL_ID}
          aria-labelledby={threadTabId(MINIMIZED_PANEL_ID, tab)}
          // Two and a half cards: the half one is the point, since a list cut
          // off mid-card is the only thing telling the user it scrolls at all.
          //   12 padding + 122 card + 12 gap + 122 card + 12 gap + 61 half
          // Bounded on the scrolling element itself, and in px rather than a
          // percentage: a scroller only scrolls when its OWN height is bounded,
          // and a percentage would depend on every ancestor up to the drawer
          // resolving a definite height. This cannot be defeated from above.
          className="max-h-[341px] min-h-0 flex-1 overflow-y-auto p-3"
        >
          <Stack gap="sm">
            {tab === 'active'
              ? renderThreadList(activeThreads, 'No active threads.')
              : renderThreadList(archivedThreads, 'No archived threads.')}
          </Stack>
        </div>
      </Paper>
    )
  },
)
